import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { mapItemsApi, notificationsApi, statsApi } from '../lib/api';
import EmergencyMap from '../components/map/EmergencyMap';
import LoadingScreen from '../components/common/LoadingScreen';
import EmptyState from '../components/common/EmptyState';
import TimeAgo from '../components/common/TimeAgo';
import StatCard from '../components/common/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { 
  Map, 
  Bell, 
  AlertTriangle, 
  Construction, 
  PartyPopper, 
  Ban,
  Users,
  MessageSquare,
  TrendingUp,
  RefreshCw
} from 'lucide-react';

const DashboardPage = () => {
  const { user, isAdmin } = useAuth();
  const { socket } = useSocket();
  const [mapItems, setMapItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const [itemsRes, notifsRes] = await Promise.all([
        mapItemsApi.getActive(),
        notificationsApi.getAll(),
      ]);
      setMapItems(itemsRes.data);
      setNotifications(notifsRes.data.slice(0, 5));

      if (isAdmin()) {
        const statsRes = await statsApi.get();
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('map_item_created', (item) => {
        setMapItems(prev => [...prev, item]);
      });
      socket.on('map_item_updated', (item) => {
        setMapItems(prev => prev.map(i => i.id === item.id ? item : i));
      });
      socket.on('map_item_deleted', ({ id }) => {
        setMapItems(prev => prev.filter(i => i.id !== id));
      });
      socket.on('map_item_toggled', (item) => {
        if (item.is_active) {
          setMapItems(prev => [...prev.filter(i => i.id !== item.id), item]);
        } else {
          setMapItems(prev => prev.filter(i => i.id !== item.id));
        }
      });
      socket.on('new_notification', (notif) => {
        setNotifications(prev => [notif, ...prev.slice(0, 4)]);
      });

      return () => {
        socket.off('map_item_created');
        socket.off('map_item_updated');
        socket.off('map_item_deleted');
        socket.off('map_item_toggled');
        socket.off('new_notification');
      };
    }
  }, [socket]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'road_closure': return <Ban className="w-4 h-4" />;
      case 'detour': return <Construction className="w-4 h-4" />;
      case 'parade_route': return <PartyPopper className="w-4 h-4" />;
      case 'closed_area': return <AlertTriangle className="w-4 h-4" />;
      default: return <Map className="w-4 h-4" />;
    }
  };

  const getItemColor = (type) => {
    switch (type) {
      case 'road_closure': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'detour': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'parade_route': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'closed_area': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-blue-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-zinc-500 text-white';
    }
  };

  const itemTypeCounts = {
    road_closure: mapItems.filter(i => i.item_type === 'road_closure').length,
    detour: mapItems.filter(i => i.item_type === 'detour').length,
    parade_route: mapItems.filter(i => i.item_type === 'parade_route').length,
    closed_area: mapItems.filter(i => i.item_type === 'closed_area').length,
  };

  if (loading) {
    return <LoadingScreen message="Loading dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-4 lg:p-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-headings text-3xl font-bold uppercase tracking-tight text-white">
            Dashboard
          </h1>
          <p className="text-zinc-400 mt-1">
            Welcome back, <span className="text-white">{user?.name}</span>
          </p>
        </div>
        <Button
          variant="outline"
          className="border-zinc-700 text-zinc-300 hover:text-white"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards - Admin Only */}
      {isAdmin() && stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6"
        >
          <StatCard title="Active Items" value={stats.active_map_items} icon={Map} color="red" />
          <StatCard title="Total Users" value={stats.total_users} icon={Users} color="blue" />
          <StatCard title="Notifications" value={stats.total_notifications} icon={Bell} color="orange" />
          <StatCard title="Messages" value={stats.total_messages} icon={MessageSquare} color="green" />
          <StatCard title="Total Items" value={stats.total_map_items} icon={TrendingUp} color="purple" />
        </motion.div>
      )}

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Map Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card className="bg-zinc-900 border-zinc-800 h-[500px] lg:h-[600px]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-headings text-lg uppercase tracking-wide text-white flex items-center gap-2">
                  <Map className="w-5 h-5 text-red-400" />
                  Live Map
                </CardTitle>
                <div className="flex gap-2">
                  {Object.entries(itemTypeCounts).map(([type, count]) => (
                    count > 0 && (
                      <Badge key={type} variant="outline" className={getItemColor(type)}>
                        {getItemIcon(type)}
                        <span className="ml-1">{count}</span>
                      </Badge>
                    )
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-2 h-[calc(100%-60px)]">
              <EmergencyMap items={mapItems} height="100%" />
            </CardContent>
          </Card>
        </motion.div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Active Incidents */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="font-headings text-lg uppercase tracking-wide text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                  Active Incidents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  {mapItems.length === 0 ? (
                    <EmptyState type="incidents" />
                  ) : (
                    <div className="space-y-3">
                      {mapItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                        >
                          <div className={`p-2 rounded-lg ${getItemColor(item.item_type)}`}>
                            {getItemIcon(item.item_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-white truncate">{item.title}</h4>
                            <p className="text-xs text-zinc-500 truncate">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Alerts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="font-headings text-lg uppercase tracking-wide text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-red-400" />
                  Recent Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  {notifications.length === 0 ? (
                    <EmptyState type="alerts" />
                  ) : (
                    <div className="space-y-3">
                      {notifications.map((notif) => (
                        <div
                          key={notif.id}
                          className="p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getPriorityColor(notif.priority)}>
                              {notif.priority}
                            </Badge>
                            <TimeAgo date={notif.created_at} className="text-xs" />
                          </div>
                          <h4 className="text-sm font-medium text-white">{notif.title}</h4>
                          <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{notif.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
